<template>
  <div class="gift-card">
    <div class="gift-card-image">
      <span class="gift-score-badge desktop-badge">{{ gift.score }} <i class="uil-coins"></i></span>
      <img :src="gift.photo_link" :alt="gift.name"/>
    </div>
    <div class="gift-card-content">
      <div class="gift-header">
        <h3 class="gift-name">{{ gift.name }}</h3>
        <span class="gift-score-badge mobile-badge">{{ gift.score }} <i class="uil-coins"></i></span>
      </div>
      <p v-if="gift.description" class="gift-description">{{ gift.description }}</p>

      <div class="gift-footer">
        <button
          :class="['gift-btn', { 'disabled': !canPurchase }]"
          :disabled="!canPurchase"
          @click="handleGetGift">
          <i class="uil-check-circle"></i> {{ $t("gift.get_it") }}
        </button>

        <div class="gift-info">
          <span v-if="coinDifference < 0" class="coin-needed">
            {{ Math.abs(coinDifference).toFixed(2) }} {{ $t('gift.coin') }} <i class="uil-coins"></i>
            {{ $t("gift.earn_more") }}
          </span>
          <span v-if="gift.stock_quantity < 1" class="out-of-stock">
            {{ $t("errors.gift.out_of_stock") || "Stokta mevcut değil" }}
          </span>
          <span v-else class="stock-info">
            {{ gift.stock_quantity }} {{ $t("gift.last_stock_quantity") }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GiftCard",
  props: {
    gift: {
      type: Object,
      required: true
    },
    userCoin: {
      type: [Number, String],
      default: 0
    }
  },
  computed: {
    coinDifference() {
      return Number(this.userCoin) - this.gift.score;
    },
    canPurchase() {
      return this.coinDifference >= 0 && this.gift.stock_quantity > 0;
    }
  },
  methods: {
    handleGetGift() {
      if (this.canPurchase) {
        this.$emit('get-gift', {
          id: this.gift.id,
          addressRequired: this.gift.address_required
        });
      }
    }
  }
}
</script>

<style scoped>
.gift-card {
  display: flex;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: none;
  overflow: hidden;
  transition: border-color 0.15s ease;
  margin-bottom: 16px;
}

.gift-card:hover {
  transform: none;
  box-shadow: none;
  border-color: #cbd5e1;
}

.gift-card-image {
  position: relative;
  width: 180px;
  min-height: 140px;
  flex-shrink: 0;
  overflow: hidden;
  background: #f3f4f6;
}

.gift-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gift-score-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background: var(--backgroundColor, #6366f1);
  color: #fff;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 1;
}

.gift-score-badge i {
  font-size: 0.9rem;
}

.gift-card-content {
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.gift-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.gift-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.3;
  flex: 1;
}

.mobile-badge {
  display: none;
}

.gift-description {
  font-size: 0.8rem;
  font-weight: 400;
  color: #6b7280;
  margin: 0 0 12px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.gift-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gift-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #fff;
  background: #10b981;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
  width: fit-content;
}

.gift-btn:hover:not(.disabled) {
  background: #059669;
}

.gift-btn.disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

.gift-info {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.coin-needed {
  font-size: 0.8rem;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 4px;
}

.coin-needed i {
  color: #f59e0b;
}

.out-of-stock {
  font-size: 0.8rem;
  color: #ef4444;
  font-weight: 500;
}

.stock-info {
  font-size: 0.8rem;
  color: #6b7280;
}

/* Tablet (1024px) */
@media (max-width: 1024px) {
  .gift-card-image {
    width: 160px;
    min-height: 130px;
  }

  .gift-card-content {
    padding: 14px;
  }

  .gift-name {
    font-size: 1rem;
  }
}

/* Small Tablet (768px) */
@media (max-width: 768px) {
  .gift-card-image {
    width: 140px;
    min-height: 120px;
  }

  .gift-card-content {
    padding: 12px;
  }

  .gift-name {
    font-size: 0.95rem;
  }

  .gift-description {
    font-size: 0.75rem;
    margin-bottom: 10px;
  }

  .gift-btn {
    padding: 6px 12px;
    font-size: 0.8rem;
  }

  .gift-score-badge {
    padding: 3px 8px;
    font-size: 0.75rem;
  }
}

/* Mobile (576px) */
@media (max-width: 576px) {
  .gift-card {
    flex-direction: row;
    align-items: stretch;
    margin-bottom: 10px;
    border-radius: 10px;
    box-shadow: none;
  }

  .gift-card:hover {
    transform: none;
  }

  .gift-card-image {
    width: 85px;
    min-width: 85px;
    min-height: unset;
    border-radius: 10px 0 0 10px;
  }

  .desktop-badge {
    display: none;
  }

  .mobile-badge {
    display: inline-flex;
    position: static;
    padding: 2px 6px;
    font-size: 0.65rem;
    border-radius: 10px;
    flex-shrink: 0;
  }

  .mobile-badge i {
    font-size: 0.7rem;
  }

  .gift-card-content {
    padding: 10px;
    justify-content: space-between;
  }

  .gift-header {
    align-items: center;
    margin-bottom: 0;
  }

  .gift-name {
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.2;
  }

  .gift-description {
    display: none;
  }

  .gift-footer {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    margin-top: 6px;
  }

  .gift-btn {
    padding: 5px 10px;
    font-size: 0.7rem;
    border-radius: 14px;
  }

  .gift-info {
    margin-top: 0;
    text-align: right;
  }

  .coin-needed,
  .out-of-stock,
  .stock-info {
    font-size: 0.65rem;
  }
}

/* Small Mobile (400px) */
@media (max-width: 400px) {
  .gift-card {
    margin-bottom: 8px;
    border-radius: 8px;
  }

  .gift-card-image {
    width: 70px;
    min-width: 70px;
    border-radius: 8px 0 0 8px;
  }

  .gift-card-content {
    padding: 8px;
  }

  .gift-name {
    font-size: 0.75rem;
  }

  .mobile-badge {
    padding: 2px 5px;
    font-size: 0.6rem;
  }

  .gift-btn {
    padding: 4px 8px;
    font-size: 0.65rem;
  }

  .coin-needed,
  .out-of-stock,
  .stock-info {
    font-size: 0.6rem;
  }
}

@media (max-width: 576px) {
  .gift-card {
    min-width: 0;
    min-height: 0;
  }

  .gift-card-image {
    width: 88px;
    min-width: 88px;
  }

  .gift-card-content {
    min-width: 0;
    padding: 12px;
  }

  .gift-name {
    min-width: 0;
    font-size: 16px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }

  .gift-description {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    font-size: 14px;
    line-height: 1.5;
  }

  .gift-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .gift-btn {
    width: 100%;
    min-height: 48px;
    justify-content: center;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.25;
  }

  .coin-needed,
  .out-of-stock,
  .stock-info {
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>
